import { FC, memo, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';

import CheckboxDefault from 'app/components/Checkbox/CheckboxDefault';
import {
  ButtonsContainer,
  CancelButton,
  CheckboxContainer,
  DropdownButton,
  DropdownContainer,
  DropdownContent,
  DropdownHeader,
  DropdownSubtitle,
  DropdownTitle,
  Item,
  ItemInfo,
  ItemSubtitle,
  ItemTitle,
  ItemsContainer,
  SkeletonCheckbox,
  SkeletonInfo,
  SkeletonItem,
  SkeletonSubtitle,
  SkeletonTitle,
  SubmitButton,
} from 'app/components/DropdownSelect/core/styles';
import useDropdownSelect from 'app/components/DropdownSelect/hooks/useDropdownSelect';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { ReactComponent as IconArrow } from 'app/assets/icons/icon-select.svg';

export interface DropdownItem {
  id: string | number;
  title: string;
  subtitle: string;
  selected?: boolean;
}

export interface DropdownSelectProps {
  title?: string;
  subtitle?: string;
  buttonText: string;
  buttonSubmitText?: string;
  buttonCancelText?: string;
  items?: DropdownItem[];
  onSubmit?: (selectedItems: DropdownItem[]) => void;
  fetchItems?: (page: number) => Promise<{ items: DropdownItem[]; hasMore: boolean }>;
  queryKey: string;
}

const DropdownSelect: FC<DropdownSelectProps> = ({
  title = 'Filter Alerts',
  subtitle = 'Select items to filter alerts',
  buttonText,
  items: initialItems = [],
  onSubmit,
  fetchItems,
  queryKey,
  buttonSubmitText = 'Submit',
  buttonCancelText = 'Cancel',
}) => {
  const {
    isOpen,
    isSubmitting,
    allItems,
    isLoading,
    loadMoreRef,
    dropdownRef,
    selectedItems,
    handleCheckboxChange,
    handleSubmit,
    handleCancel,
    handleToggleDropdown,
    isFetchingNextPage,
  } = useDropdownSelect({ queryKey, fetchItems, initialItems, onSubmit });

  const isChanged = useMemo(() => {
    return allItems.some((item) => {
      const wasSelected = !!item.selected;
      const isNowSelected = !!selectedItems[item.id];
      return wasSelected !== isNowSelected;
    });
  }, [allItems, selectedItems]);

  const handleItemClick = (id: string | number) => () => {
    handleCheckboxChange(id);
  };

  const handleCheckboxClick =
    (id: string | number) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      handleCheckboxChange(id);
    };

  return (
    <>
      <DropdownContainer ref={dropdownRef}>
        <DropdownButton onClick={handleToggleDropdown}>
          {buttonText}
          <IconArrow className="mb-1" />
        </DropdownButton>
        <DropdownContent isOpen={isOpen}>
          <DropdownHeader>
            <DropdownTitle className="text-overflow-ellipsis">{title}</DropdownTitle>
            <DropdownSubtitle className="text-overflow-ellipsis">{subtitle}</DropdownSubtitle>
          </DropdownHeader>
          <ItemsContainer>
            {allItems.map((item) => (
              <Item key={item.id} onClick={handleItemClick(item.id)}>
                <ItemInfo>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemSubtitle>{item.subtitle}</ItemSubtitle>
                </ItemInfo>
                <CheckboxContainer onClick={handleCheckboxClick(item.id)}>
                  <CheckboxDefault checked={!!selectedItems[item.id]} onChange={() => {}} />
                </CheckboxContainer>
              </Item>
            ))}
            {isLoading && (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonItem key={`skeleton-${i}`}>
                    <SkeletonInfo>
                      <SkeletonTitle />
                      <SkeletonSubtitle />
                    </SkeletonInfo>
                    <SkeletonCheckbox />
                  </SkeletonItem>
                ))}
              </>
            )}
            {isFetchingNextPage && (
              <>
                {Array.from({ length: 2 }).map((_, i) => (
                  <SkeletonItem key={`skeleton-next-${i}`}>
                    <SkeletonInfo>
                      <SkeletonTitle />
                      <SkeletonSubtitle />
                    </SkeletonInfo>
                    <SkeletonCheckbox />
                  </SkeletonItem>
                ))}
              </>
            )}
            <div ref={loadMoreRef} className="h-40px" />
          </ItemsContainer>
          <ButtonsContainer>
            <CancelButton onClick={handleCancel}>{buttonCancelText}</CancelButton>
            <SubmitButton onClick={handleSubmit} disabled={isSubmitting || !isChanged}>
              {isSubmitting && <Spinner animation="border" size="sm" />}
              {buttonSubmitText}
            </SubmitButton>
          </ButtonsContainer>
        </DropdownContent>
      </DropdownContainer>
    </>
  );
};

export default memo(DropdownSelect);
